import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../Auth/AuthContext';

const HomePage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // Removed excessive debug logging that was causing performance issues

  const handleDashboardClick = (e) => {
    e.preventDefault();
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/signin');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center">
            <h1 className="text-2xl font-bold text-gray-900">DescribeAPI</h1>
            <nav className="hidden md:flex ml-10 space-x-8">
              <a href="#features" className="text-gray-600 hover:text-blue-600 transition-colors">Features</a>
              <a href="#pricing" className="text-gray-600 hover:text-blue-600 transition-colors">Pricing</a>
              <a href="#api" className="text-gray-600 hover:text-blue-600 transition-colors">API</a>
            </nav>
          </div>
          
          <div className="flex items-center space-x-4">
            {loading ? (
              <div className="text-gray-500">Loading...</div>
            ) : user ? (
              <button 
                onClick={handleDashboardClick}
                className="text-blue-600 hover:text-blue-800 font-medium cursor-pointer"
              >
                Dashboard
              </button>
            ) : (
              <>
                <Link to="/signin" className="text-gray-600 hover:text-gray-900">
                  Sign In
                </Link>
                <Link to="/signup" className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded">
                  Sign Up
                </Link>
              </>
            )}
          </div>
        </div>
      </header>
      
      {/* Hero Section */}
      <div className="bg-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                AI-Powered Image Descriptions
              </h2>
              <p className="text-xl mb-6">
                Get detailed descriptions of images in 20+ languages with both text and audio output.
              </p>
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                <Link to="/signup" className="bg-white text-blue-600 hover:bg-gray-100 px-6 py-3 rounded-lg font-medium text-center">
                  Get Started Free
                </Link>
                <a href="#features" className="border border-white text-white hover:bg-blue-700 px-6 py-3 rounded-lg font-medium text-center">
                  Learn More
                </a>
              </div>
            </div>
            {/* Removed problematic image */}
          </div>
        </div>
      </div>
      
      {/* Features Section */}
      <div id="features" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Powerful Features</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our API provides comprehensive image description capabilities for developers and businesses.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="text-blue-500 mb-4">
                <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Text Descriptions</h3>
              <p className="text-gray-600">
                Get detailed text descriptions of any image with our advanced AI model.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="text-blue-500 mb-4">
                <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Audio Descriptions</h3>
              <p className="text-gray-600">
                Convert image descriptions to natural-sounding speech in multiple languages.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="text-blue-500 mb-4">
                <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7 2a1 1 0 011 1v1h3a1 1 0 110 2H9.578a18.87 18.87 0 01-1.724 4.78c.29.354.596.696.914 1.026a1 1 0 11-1.44 1.389c-.188-.196-.373-.396-.554-.6a19.098 19.098 0 01-3.107 3.567 1 1 0 01-1.334-1.49 17.087 17.087 0 003.13-3.733 18.992 18.992 0 01-1.487-2.494 1 1 0 111.79-.89c.234.47.489.928.764 1.372.417-.934.752-1.913.997-2.927H3a1 1 0 110-2h3V3a1 1 0 011-1zm6 6a1 1 0 01.894.553l2.991 5.982a.869.869 0 01.02.037l.99 1.98a1 1 0 11-1.79.895L15.383 16h-4.764l-.724 1.447a1 1 0 11-1.788-.894l.99-1.98.019-.038 2.99-5.982A1 1 0 0113 8zm-1.382 6h2.764L13 11.236 11.618 14z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Multi-Language Support</h3>
              <p className="text-gray-600">
                Support for 20+ languages with language-specific voices for audio output.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Pricing Section */}
      <div id="pricing" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Simple, Transparent Pricing</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the plan that fits your needs - text and audio descriptions priced separately
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-md border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Free</h3>
              <p className="text-4xl font-bold mb-4">$0<span className="text-lg font-normal text-gray-500">/month</span></p>
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="text-sm text-gray-600 space-y-1">
                  <div className="flex justify-between">
                    <span>Text descriptions:</span>
                    <span className="font-semibold">100/month</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Audio descriptions:</span>
                    <span className="font-semibold">50/month</span>
                  </div>
                </div>
              </div>
              <ul className="mb-8 space-y-2">
                <li className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>20+ languages supported</span>
                </li>
                <li className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>50+ voice options</span>
                </li>
                <li className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>Standard support</span>
                </li>
                <li className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>99% uptime SLA</span>
                </li>
              </ul>
              <Link to="/signup" className="block w-full bg-blue-500 hover:bg-blue-600 text-white text-center py-2 px-4 rounded">
                Get Started
              </Link>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-md border-2 border-blue-500 transform scale-105">
              <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-bl">
                POPULAR
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Professional</h3>
              <p className="text-4xl font-bold mb-4">$10<span className="text-lg font-normal text-gray-500">/month</span></p>
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="text-sm text-gray-600 space-y-1">
                  <div className="flex justify-between">
                    <span>Text descriptions:</span>
                    <span className="font-semibold">500/month</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Audio descriptions:</span>
                    <span className="font-semibold">250/month</span>
                  </div>
                  <div className="border-t pt-2 mt-2">
                    <div className="text-xs text-center text-blue-600">
                      Need more? Purchase additional credits in $10 increments
                    </div>
                  </div>
                </div>
              </div>
              <ul className="mb-8 space-y-2">
                <li className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>Everything in Free</span>
                </li>
                <li className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>Priority support</span>
                </li>
                <li className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>99.9% uptime SLA</span>
                </li>
                <li className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>Usage analytics</span>
                </li>
              </ul>
              <Link to="/signup" className="block w-full bg-blue-500 hover:bg-blue-600 text-white text-center py-2 px-4 rounded">
                Get Started
              </Link>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-md border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Enterprise</h3>
              <p className="text-4xl font-bold mb-4">Custom<span className="text-lg font-normal text-gray-500">/month</span></p>
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="text-sm text-gray-600 space-y-1">
                  <div>Custom volume pricing</div>
                  <div>Dedicated infrastructure</div>
                  <div>SLA guarantees</div>
                </div>
              </div>
              <ul className="mb-8 space-y-2">
                <li className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>Everything in Professional</span>
                </li>
                <li className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>Dedicated support team</span>
                </li>
                <li className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>99.99% uptime SLA</span>
                </li>
                <li className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>Custom integrations</span>
                </li>
              </ul>
              <a href="mailto:sales@describeapi.com" className="block w-full bg-gray-200 hover:bg-gray-300 text-gray-800 text-center py-2 px-4 rounded">
                Contact Sales
              </a>
            </div>
          </div>
          
          <div className="mt-12 text-center">
            <p className="text-sm text-gray-500">
              All plans include access to 20+ languages and 50+ voice options. 
              Additional credits available in $10 increments. No setup fees or hidden charges.
            </p>
          </div>
        </div>
      </div>
      
      {/* API Reference Section */}
      <div id="api" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">API Reference</h3>
            <p className="text-lg text-gray-600">Complete documentation for both text and audio endpoints</p>
          </div>

          {/* Base URL */}
          <div className="mb-12">
            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="text-xl font-semibold text-gray-900 mb-3">Base URL</h4>
              <div className="bg-gray-900 rounded-lg p-4">
                <code className="text-green-400 text-lg">https://api.describeapi.com</code>
              </div>
            </div>
          </div>

          {/* Text Description Endpoint */}
          <div className="mb-16">
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-green-50 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold">POST</span>
                  <h4 className="text-xl font-semibold text-gray-900">/describe/text</h4>
                </div>
                <p className="text-gray-600 mt-2">Get detailed text descriptions of images in 20+ languages</p>
              </div>
              
              <div className="p-6">
                {/* Request */}
                <div className="mb-8">
                  <h5 className="text-lg font-semibold text-gray-900 mb-4">Request</h5>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h6 className="font-medium text-gray-900 mb-2">Headers</h6>
                      <div className="bg-gray-900 rounded-lg p-4 text-sm">
                        <div className="text-blue-400">Content-Type: <span className="text-green-400">application/json</span></div>
                        <div className="text-blue-400">X-Api-Key: <span className="text-green-400">YOUR_API_KEY</span></div>
                      </div>
                    </div>
                    <div>
                      <h6 className="font-medium text-gray-900 mb-2">Parameters</h6>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="font-medium">image</span>
                          <span className="text-red-500">required</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">language</span>
                          <span className="text-gray-500">optional</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <h6 className="font-medium text-gray-900 mb-2">Request Body</h6>
                    <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                      <pre className="text-green-400 text-sm">
{`{
  "image": "base64_encoded_image_data",
  "language": "es"  // Optional: ISO 639-1 code (default: "en")
}`}
                      </pre>
                    </div>
                  </div>
                </div>

                {/* Response */}
                <div className="mb-8">
                  <h5 className="text-lg font-semibold text-gray-900 mb-4">Response</h5>
                  <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                    <pre className="text-green-400 text-sm">
{`{
  "description": "Una hermosa puesta de sol sobre un paisaje montañoso...",
  "format": "text",
  "language": "es"
}`}
                    </pre>
                  </div>
                </div>

                {/* Code Examples */}
                <div>
                  <h5 className="text-lg font-semibold text-gray-900 mb-4">Code Examples</h5>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h6 className="font-medium text-gray-900 mb-2">JavaScript</h6>
                      <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                        <pre className="text-green-400 text-sm">
{`const response = await fetch(
  'https://api.describeapi.com/describe/text',
  {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Api-Key': 'YOUR_API_KEY'
    },
    body: JSON.stringify({
      image: imageBase64,
      language: 'es'
    })
  }
);

const result = await response.json();
// Process the result
return result.description;`}
                        </pre>
                      </div>
                    </div>
                    <div>
                      <h6 className="font-medium text-gray-900 mb-2">Python</h6>
                      <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                        <pre className="text-green-400 text-sm">
{`import requests
import json

response = requests.post(
    'https://api.describeapi.com/describe/text',
    headers={
        'Content-Type': 'application/json',
        'X-Api-Key': 'YOUR_API_KEY'
    },
    json={
        'image': image_base64,
        'language': 'es'
    }
)

result = response.json()
print(result['description'])`}
                        </pre>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Audio Description Endpoint */}
          <div className="mb-16">
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-blue-50 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-semibold">POST</span>
                  <h4 className="text-xl font-semibold text-gray-900">/describe/audio</h4>
                </div>
                <p className="text-gray-600 mt-2">Get audio descriptions with natural-sounding voices in multiple languages</p>
              </div>
              
              <div className="p-6">
                {/* Request */}
                <div className="mb-8">
                  <h5 className="text-lg font-semibold text-gray-900 mb-4">Request</h5>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h6 className="font-medium text-gray-900 mb-2">Headers</h6>
                      <div className="bg-gray-900 rounded-lg p-4 text-sm">
                        <div className="text-blue-400">Content-Type: <span className="text-green-400">application/json</span></div>
                        <div className="text-blue-400">X-Api-Key: <span className="text-green-400">YOUR_API_KEY</span></div>
                      </div>
                    </div>
                    <div>
                      <h6 className="font-medium text-gray-900 mb-2">Parameters</h6>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="font-medium">image</span>
                          <span className="text-red-500">required</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">language</span>
                          <span className="text-gray-500">optional</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">voice</span>
                          <span className="text-gray-500">optional</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <h6 className="font-medium text-gray-900 mb-2">Request Body</h6>
                    <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                      <pre className="text-blue-400 text-sm">
{`{
  "image": "base64_encoded_image_data",
  "language": "ja",     // Optional: ISO 639-1 code (default: "en")
  "voice": "Mizuki"     // Optional: Voice ID (auto-selected by language)
}`}
                      </pre>
                    </div>
                  </div>
                </div>

                {/* Response */}
                <div className="mb-8">
                  <h5 className="text-lg font-semibold text-gray-900 mb-4">Response</h5>
                  <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                    <pre className="text-blue-400 text-sm">
{`{
  "description": "この画像には美しい夕日が山の風景に映っています...",
  "audio": "base64_encoded_mp3_data",
  "format": "audio",
  "voice": "Mizuki",
  "language": "ja"
}`}
                    </pre>
                  </div>
                </div>

                {/* Code Examples */}
                <div>
                  <h5 className="text-lg font-semibold text-gray-900 mb-4">Code Examples</h5>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h6 className="font-medium text-gray-900 mb-2">JavaScript</h6>
                      <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                        <pre className="text-blue-400 text-sm">
{`const response = await fetch(
  'https://api.describeapi.com/describe/audio',
  {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Api-Key': 'YOUR_API_KEY'
    },
    body: JSON.stringify({
      image: imageBase64,
      language: 'ja',
      voice: 'Mizuki'
    })
  }
);

const result = await response.json();

// Play audio
const audio = new Audio(
  \`data:audio/mp3;base64,\${result.audio}\`
);
audio.play();`}
                        </pre>
                      </div>
                    </div>
                    <div>
                      <h6 className="font-medium text-gray-900 mb-2">Python</h6>
                      <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                        <pre className="text-blue-400 text-sm">
{`import requests
import base64

response = requests.post(
    'https://api.describeapi.com/describe/audio',
    headers={
        'Content-Type': 'application/json',
        'X-Api-Key': 'YOUR_API_KEY'
    },
    json={
        'image': image_base64,
        'language': 'ja',
        'voice': 'Mizuki'
    }
)

result = response.json()

# Save audio file
audio_data = base64.b64decode(result['audio'])
with open('description.mp3', 'wb') as f:
    f.write(audio_data)`}
                        </pre>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* CTA Section */}
      <div className="bg-blue-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to get started?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Sign up for free and start using our API today. No credit card required.
          </p>
          <Link to="/signup" className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-3 rounded-lg font-medium inline-block">
            Create Free Account
          </Link>
        </div>
      </div>
      
      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-lg font-bold mb-4">DescribeAPI</h3>
              <p className="text-gray-400">
                AI-powered image descriptions in multiple languages with text and audio output.
              </p>
            </div>
            
            <div>
              <h4 className="text-lg font-bold mb-4">Product</h4>
              <ul className="space-y-2">
                <li><a href="#features" className="text-gray-400 hover:text-white">Features</a></li>
                <li><a href="#pricing" className="text-gray-400 hover:text-white">Pricing</a></li>
                <li><a href="#api" className="text-gray-400 hover:text-white">API Reference</a></li>
                <li><button onClick={handleDashboardClick} className="text-gray-400 hover:text-white cursor-pointer">Dashboard</button></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-bold mb-4">Support</h4>
              <ul className="space-y-2">
                <li><a href="mailto:support@describeapi.com" className="text-gray-400 hover:text-white">Contact Support</a></li>
                <li><a href="mailto:sales@describeapi.com" className="text-gray-400 hover:text-white">Contact Sales</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center">
            <p className="text-gray-400">
              &copy; {new Date().getFullYear()} DescribeAPI. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
